/**
 * MessageThread - one conversation, about one job or one offer
 * ---------------------------------------------------------------
 * The two people doing a job sorting out the real details: which door, what to
 * bring, running late.
 *
 * Since 25 September 2026 the same screen also carries a question about a
 * student's OFFER, which is the one conversation on this site an adult may
 * start. It is the same screen on purpose - a conversation is a conversation,
 * and a second one would be a second place for the standing notice, the
 * contact-details warning and the Report button to drift out of step. What
 * changes is the subject line at the top, and that an adult waiting for a
 * first answer is shown why they cannot write again.
 *
 * THREE THINGS THIS SCREEN SAYS OUT LOUD, AND SHOULD KEEP SAYING
 *
 * 1. That messages are kept and can be read if reported. Nobody should be
 *    surprised by that later, and knowing it is itself a deterrent. It sits at
 *    the top of the conversation itself, the way every messaging app puts its
 *    standing notice - read on the way in, and still there if you scroll back.
 * 2. A warning when somebody types a phone number, because a conversation that
 *    moves to WhatsApp leaves no record at all - which is exactly where harm
 *    happens unseen. A warning, not a block: there are honest reasons to give
 *    a number, and a site that refuses is one people work around.
 * 3. Report, always within reach, never buried in a menu. It is in the header,
 *    which is pinned, so it is on screen for every message ever read.
 *
 * SHAPE
 * This is the one screen on the site that fills the window rather than
 * flowing down the page (see isConversation in App.jsx). Header pinned,
 * messages the only thing that scrolls, compose box on the bottom edge under
 * your thumb. It used to be an ordinary page, which on a phone put the box you
 * type into below the fold with the site footer underneath it.
 */

import { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Send, Lock, ShieldAlert, ChevronLeft, ShieldCheck, Archive, HelpCircle } from 'lucide-react';

import { messageApi, badgesChanged } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { dateTime, timeAgo } from '../utils/format';
import Loader from '../components/Loader';
import Avatar from '../components/Avatar';
import ReportButton from '../components/ReportButton';
import { useI18n } from '../hooks/useI18n';

/**
 * The same check the server makes, so the warning appears as somebody types
 * rather than after they have already sent it. The server's copy is the one
 * that counts; this one only exists to be helpful in time.
 */
function looksLikeContactDetails(text) {
  const s = String(text || '');
  if (/[\w.+-]+@[\w-]+\.[\w.]{2,}/.test(s)) return true;
  const runs = s.match(/[\d][\d\s.\-()]{6,}[\d]/g) || [];
  return runs.some((run) => run.replace(/\D/g, '').length >= 8);
}

/**
 * How many lines the compose box grows to before it stops and starts
 * scrolling instead. Six is about as much of the screen as a message is
 * worth taking; past that the conversation you are replying to has gone.
 */
const MAX_COMPOSE_LINES = 6;

export default function MessageThread() {
  const { t } = useI18n();
  const { isStudent } = useAuth();
  /**
   * WHICH KIND OF CONVERSATION. /messages/<choreId> is a job;
   * /messages/offer/<offerId> is a question about a student's availability.
   */
  const { choreId, offerId } = useParams();
  const isOffer = Boolean(offerId);
  /**
   * Which conversation about this chore. A poster can have one with each
   * student who asked about the same job, so the link carries the student -
   * see backend/routes/messages.js. A student's own link never has it.
   */
  const [params] = useSearchParams();
  // On a job this names the student; on an offer it names the adult. Either
  // way it is "which of the conversations about this thing".
  const withStudent = params.get('with') || undefined;
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scroller = useRef(null);
  const box = useRef(null);

  /**
   * `quiet` is a refresh nobody asked for - the timer below.
   *
   * It must not show a spinner and must not replace the screen with an
   * error: a conversation that has been on screen for ten minutes should
   * not turn into "could not reach the server" because one poll missed
   * while a phone changed cell. The next tick will pick it up.
   */
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      try {
        const thread = isOffer
          ? await messageApi.offerThread(offerId, withStudent)
          : await messageApi.thread(choreId, withStudent);
        /**
         * Replace only when something actually changed.
         *
         * Handing React a new object every few seconds would re-render the
         * whole conversation on a timer, and the layout effect below moves
         * the scroller whenever the message count changes - so an idle
         * thread would keep yanking itself to the bottom while somebody was
         * reading further up.
         */
        setData((current) => {
          if (!current) return thread;
          const same =
            current.messages.length === thread.messages.length &&
            current.open === thread.open &&
            current.messages[current.messages.length - 1]?._id ===
              thread.messages[thread.messages.length - 1]?._id;
          return same ? current : thread;
        });
        // Mark what they have now seen. Not awaited - a failure here should not
        // stop the conversation appearing.
        (isOffer
          ? messageApi.offerMarkRead(offerId, withStudent)
          : messageApi.markRead(choreId, withStudent))
          .then(() => badgesChanged())
          .catch(() => {});
        if (!quiet) setError('');
      } catch (err) {
        if (!quiet) setError(err.message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [choreId, offerId, isOffer, withStudent]
  );

  useEffect(() => {
    load();
  }, [load]);

  /**
   * KEEP THE CONVERSATION LIVE WHILE IT IS OPEN.
   *
   * Until this existed a message arriving while you were looking at the
   * thread did not appear: you got a notification, and had to leave the
   * conversation and come back to read the thing you were already staring
   * at. That reads as the site being broken, and it is the one screen where
   * people expect the opposite.
   *
   * Five seconds, and only while the tab is actually being looked at.
   * Browsers throttle timers in a background tab anyway, and a conversation
   * nobody is watching does not need to be current - it is re-fetched the
   * moment they look back at it.
   *
   * Polling rather than a socket on purpose. A websocket means another
   * moving part on the server, another thing to keep alive behind
   * Cloudflare and Caddy, and another thing that can fail quietly - for a
   * site where two people occasionally sort out which door to knock on.
   * See the same reasoning about libraries in useI18n.jsx.
   *
   * The server reads these requests as "this person is here" and skips the
   * notification for a message they are watching arrive - see
   * THREAD_PRESENCE_MS in backend/routes/messages.js.
   */
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };

    const timer = setInterval(tick, 5000);
    // Coming back to the tab should be instant, not up to five seconds.
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  // Open on the newest message, and stay there as new ones arrive.
  //
  // The scroller is moved directly rather than with scrollIntoView, which
  // scrolls every ancestor that can move - including the window - and on a
  // phone that dragged the whole screen up by the height of the navbar.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [data?.messages?.length]);

  /**
   * Grow the compose box to fit what is being typed, up to six lines.
   *
   * A fixed two-row box is wrong in both directions on a phone: mostly too
   * tall for the one line people actually send, and too short the moment
   * somebody writes an address.
   *
   * The limit is worked out from the line height rather than written down as
   * a number of pixels, so it stays six lines if the type size ever changes.
   */
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 22;
    const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const frame = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + borders;

    // Let it fall back to its natural size, measure that, then clamp.
    //
    // The borders are added back on purpose. Everything on this site is
    // `box-sizing: border-box`, so a height we set has to include the border
    // - but scrollHeight does not count it. Without this the box is two
    // pixels short of its own contents and scrolls by that much for ever,
    // which looks like a line of text being clipped.
    el.style.height = 'auto';
    const natural = el.scrollHeight + borders;
    el.style.height = `${Math.min(natural, lineHeight * MAX_COMPOSE_LINES + frame)}px`;

    // Past the sixth line the box stops growing, so the earlier lines have to
    // scroll up out of sight - otherwise you are typing on a line you cannot
    // see. Only when the cursor is at the very end, though: somebody fixing a
    // typo three lines back should not be dragged to the bottom.
    const atEnd =
      el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
    if (atEnd) el.scrollTop = el.scrollHeight;
  }, [draft]);

  const send = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;

    setSending(true);
    setError('');
    try {
      const result = isOffer
        ? await messageApi.offerSend(offerId, body, withStudent)
        : await messageApi.send(choreId, body, withStudent);
      setData((d) => ({ ...d, messages: [...d.messages, result.message] }));
      setDraft('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Loader label={t('thread.loading')} />;

  if (!data) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error || t('thread.none')}</div>
      </div>
    );
  }

  const { other, chore, offer, messages, open, closedReason, enquiry } = data;

  /**
   * An adult who has asked and is waiting. They get one question until the
   * student answers - see backend/routes/messages.js - so the box is replaced
   * by the reason rather than left there to be refused.
   */
  const waiting = Boolean(data.awaitingReply);

  /**
   * Filing it away is per person and silent: the other side sees no change,
   * and anything said afterwards brings it back by itself. See
   * backend/models/ThreadState.js.
   */
  const archive = async () => {
    try {
      if (isOffer) await messageApi.offerArchive(offerId, withStudent);
      else await messageApi.archive(choreId, withStudent);
      navigate('/messages');
    } catch (err) {
      setError(err.message);
    }
  };
  const warn = looksLikeContactDetails(draft);

  return (
    <div className="chat">
      {/* ---------------- WHO AND WHAT ---------------- */}
      <header className="chat-header">
        <Link to="/messages" className="chat-back" aria-label={t('thread.backToMessages')}>
          <ChevronLeft size={22} aria-hidden="true" />
        </Link>

        <Avatar person={other} size="sm" />

        <div className="chat-who">
          <strong>
            {other.firstName} {other.lastName?.[0]}.
          </strong>
          <span className="chat-about">
            {isOffer
              ? [t('thread.aboutTheirTimes'), offer?.city].filter(Boolean).join(' \u00b7 ')
              : `${chore.title} \u00b7 ${dateTime(chore.scheduledFor)}`}
          </span>
        </div>

        <button
          type="button"
          className="chat-archive"
          onClick={archive}
          title={t('thread.archive')}
          aria-label={t('thread.archive')}
        >
          <Archive size={18} aria-hidden="true" />
        </button>

        {/* A question about an offer has no chore to name, so the report is
            about the person - which is what the button does anyway. */}
        <ReportButton
          aboutUser={other._id}
          aboutChore={isOffer ? undefined : chore._id}
          personName={other.firstName}
        />
      </header>

      {/* ---------------- THE CONVERSATION ---------------- */}
      <div className="chat-scroll" ref={scroller}>
        {/* Said once, plainly, above the first message - where a messaging app
            puts a standing notice, and where somebody opening the thread for
            the first time cannot miss it. */}
        <p className="chat-notice">
          <ShieldCheck size={14} aria-hidden="true" />
          {t('thread.keptNotice')}
        </p>

        {enquiry && (
          <p className="chat-notice">
            <HelpCircle size={14} aria-hidden="true" />
            {(() => {
              if (isOffer) {
                return isStudent
                  ? t('thread.theyAskedAboutYourTimes', { name: other.firstName })
                  : t('thread.youAskedAboutTheirTimes');
              }
              return isStudent
                ? t('thread.askingAbout')
                : t('thread.theyAreAsking', { name: other.firstName });
            })()}
          </p>
        )}

        {messages.length === 0 ? (
          <p className="chat-empty">
            {t('thread.noMessagesYet')}
          </p>
        ) : (
          messages.map((m) => (
            <div key={m._id} className={`message-row${m.mine ? ' is-mine' : ''}`}>
              <div className="message-bubble">
                <p>{m.body}</p>
                <span className="message-time">{timeAgo(m.createdAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ---------------- WRITING ---------------- */}
      {/* eslint-disable-next-line no-nested-ternary */}
      {waiting ? (
        <div className="chat-closed">
          <Lock size={18} aria-hidden="true" />
          <div className="text-sm">{t('thread.waitingForAnswer', { name: other.firstName })}</div>
        </div>
      ) : open ? (
        <form onSubmit={send} className="chat-compose">
          {error && <div className="alert alert-error">{error}</div>}

          {warn && (
            <div className="alert alert-warning">
              <ShieldAlert size={18} aria-hidden="true" />
              <div className="text-sm">
                {t('thread.phoneWarning')}
              </div>
            </div>
          )}

          <div className="chat-compose-row">
            <label className="sr-only" htmlFor="message-body">{t('thread.yourMessage')}</label>
            <textarea
              id="message-body"
              ref={box}
              rows={1}
              value={draft}
              maxLength={2000}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('thread.messagePlaceholder')}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter makes a new line. On a phone the
                // on-screen keyboard's return key inserts a newline as usual,
                // because there is no shift to hold - hence the button too.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(e);
                }
              }}
            />
            <button
              type="submit"
              className="chat-send"
              disabled={sending || !draft.trim()}
              aria-label={t('thread.send')}
            >
              <Send size={18} aria-hidden="true" />
            </button>
          </div>
        </form>
      ) : (
        <div className="chat-closed">
          <Lock size={18} aria-hidden="true" />
          <div className="text-sm">
            {(() => {
              if (closedReason === 'cancelled') return t('thread.closedCancelled');
              if (closedReason === 'taken') return t('thread.closedTaken');
              if (closedReason === 'offer-gone') return t('thread.closedOfferGone');
              if (closedReason === 'offers-off') return t('thread.closedOffersOff');
              return t('thread.closedFinished');
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
